import React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { useSession } from '../lib/SessionContext';
import { t } from '../i18n';
import { confirmLeaving } from '../lib/unsavedChanges';
import DetailsTab from './settings/DetailsTab';
import HoursTab from './settings/HoursTab';
import ChargesTab from './settings/ChargesTab';
import NoticesTab from './settings/NoticesTab';
import PracticeTab from './settings/PracticeTab';

/**
 * Everything a professional sets, in one place: what is theirs in four tabs,
 * and for admin a fifth with what applies to the whole practice.
 *
 * It replaced a split. A professional's settings were in Mi perfil, which the
 * professional menu did not list and a phone could not open at all, and in a
 * tab of Datos maestros; Configuración was admin's alone. Patients keep Mi
 * perfil, which holds only their contact details and emails.
 *
 * Each tab has its own address, so a link can point at one. Only the open tab
 * is mounted, so leaving a tab with a card unsaved asks first.
 */

const TABS = [
  { id: 'datos', label: 'settings.tabs.details', Tab: DetailsTab },
  { id: 'horarios', label: 'settings.tabs.hours', Tab: HoursTab },
  { id: 'cobros', label: 'settings.tabs.charges', Tab: ChargesTab },
  { id: 'avisos', label: 'settings.tabs.notices', Tab: NoticesTab },
  { id: 'consultorio', label: 'settings.tabs.practice', Tab: PracticeTab, adminOnly: true },
];

const pathOf = (id) => (id === TABS[0].id ? '/configuracion' : `/configuracion/${id}`);

const SettingsView = () => {
  const { tab: requested } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useSession();

  const tabs = TABS.filter((tab) => isAdmin || !tab.adminOnly);
  const current = requested ? tabs.find((tab) => tab.id === requested) : tabs[0];
  // An unknown tab, or Consultorio for a professional who is not admin.
  if (!current) return <Navigate to="/configuracion" replace />;

  const open = (tab) => {
    if (tab.id === current.id || !confirmLeaving()) return;
    navigate(pathOf(tab.id));
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
          <Settings size={24} className="text-slate-400" />{t('settings.title')}
        </h1>
        <p className="text-sm text-slate-500">{t(isAdmin ? 'settings.subtitleAdmin' : 'settings.subtitleTherapist')}</p>
      </div>

      <div role="tablist" className="flex flex-wrap gap-1 border-b border-slate-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tab.id === current.id}
            onClick={() => open(tab)}
            className={`border-b-2 px-3 py-2 text-sm font-medium transition sm:px-4 ${
              tab.id === current.id
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t(tab.label)}
          </button>
        ))}
      </div>

      <current.Tab key={current.id} />
    </div>
  );
};

export default SettingsView;
